import React from "react";
import { useLocation } from "react-router-dom";

import { withConfig } from "../../utilities/appConfig";
import { assistAgentEndpoints } from "../../config";
import { withSession } from "../session";
import { APP_ROLES } from "../constants";
import { AssistProvider, AssistBox, AssistModal } from "./shared";
import { AssistProjectTypeContext } from "./visibilityContext";

// This is the api-central specific component
// const allowedEmails = [
//   "kiran.kumar.paladugu@adp.com",
//   "michael.greenberg@adp.com",
//   "Mike.Dwillis@ADP.com",
//   "michael.esposito@ADP.com",
//   "Nate.Gautier@adp.com",
//   "Billy.Jahn@ADP.com",
//   "Jim.Kontos@ADP.com",
//   "cory.lamle@ADP.com",
//   "Jason.Meriwether@adp.com",
//   "Julia.Thay@adp.com",
//   "Poojitha.Ganji@ADP.com",
//   "Foram.Shah@ADP.com",
//   "winfred.allen@ADP.com",
//   "nick.bodall@adp.com",
//   "mike.bowers@adp.com",
//   "rob.oostdijk@adp.com",
//   "raymond.ruesink@adp.com",
//   "Bastiaan.Hebing@adp.com",
//   "Nicolas.Bourget@adp.com",
//   "ahmed.mejbri@adp.com",
//   "bryce.deneen@adp.com",
//   "shawn.milton@adp.com",
//   "don.cholish@adp.com",
//   "justin.howell@adp.com",
//   "ashok.gadikoppula@adp.com",
//   "Tanmay.Jhawar@ADP.com",
//   "thiago.pereira@adp.com",
//   "bud.walder@ADP.com",
//   "Siddhant.A.Mohapatra@adp.com",
//   "collin.potter@ADP.com",
// ];

/**
 * @param {{
 *  config?: { assistfeatureflag?: { enabled: boolean } },
 *  session?: {
 *    given_name?: string,
 *    family_name?: string,
 *    roles: Array<string>,
 *    email: string,
 *    region: string,
 *    disableApicDevAgent?: boolean
 *  },
 *  children: React.ReactNode
 * }} props
 */
const MarketplaceAssist = ({ config, session, children }) => {
  const location = useLocation();
  const [reportedProjectTypes, setReportedProjectTypes] = React.useState({});
  const envEndpoints = assistAgentEndpoints[import.meta.env.VITE_ENVIRONMENT] || {};
  const assistEndpoints = envEndpoints[session?.region === "eu" ? "eu" : "us"] || {};
  const initials = `${session?.given_name?.charAt(0)}${session?.family_name?.charAt(0)}`.toUpperCase();
  const isMcpProjectRoute = location.pathname.startsWith("/mcp-projects/");
  const adminProjectMatch = location.pathname.match(/^\/[^/]+\/client-projects\/([^/]+)(?:\/.*)?$/);
  const regularProjectMatch = location.pathname.match(/^\/projects\/([^/]+)(?:\/.*)?$/);
  const routeProjectID = adminProjectMatch?.[1] || regularProjectMatch?.[1] || null;
  const isSharedProjectRoute = Boolean(routeProjectID);
  const reportedProjectType = routeProjectID ? reportedProjectTypes[routeProjectID] : null;
  const reportProjectType = React.useCallback((projectType) => {
    if (!routeProjectID) {
      return;
    }

    setReportedProjectTypes((currentTypes) => {
      if (projectType) {
        return {
          ...currentTypes,
          [routeProjectID]: projectType,
        };
      }

      const nextTypes = { ...currentTypes };
      delete nextTypes[routeProjectID];
      return nextTypes;
    });
  }, [routeProjectID]);

  const isAdmin = session?.roles?.some(role => {
    return role === APP_ROLES.SUPER_ADMIN || role === APP_ROLES.TEMPLATE_ADMIN || role === APP_ROLES.CLIENT_ADMIN ||
      role === APP_ROLES.ESI_SUPER_ADMIN || role === APP_ROLES.ESI_TEMPLATE_ADMIN || role === APP_ROLES.ESI_CLIENT_ADMIN;
  }) || false;

  const isAllowedRegion = session?.region === "us" || session?.region === "eu";
  const isAssistEnabled = config.assistfeatureflag?.enabled
    && isAllowedRegion
    && !session?.disableApicDevAgent
    && !session?.isCAOrg;
  const showAssist = isAssistEnabled
    && (isAdmin || location.pathname.startsWith("/project"))
    && !isMcpProjectRoute
    && reportedProjectType !== "SIMPLIFIED_MCP"
    && (!isSharedProjectRoute || Boolean(reportedProjectType));

  // const isAllowedEmail = import.meta.env.MODE === "prod" ? false : allowedEmails.some(email => {
  //   return email.toLocaleLowerCase() === session?.email?.toLocaleLowerCase();
  // }) || false;

  return (
    <AssistProjectTypeContext.Provider value={reportProjectType}>
      {isAssistEnabled ? (
        <AssistProvider {...assistEndpoints} {...{ isAdmin, initials }} region={session?.region}>
          <>
            {showAssist && (
              <div className="mx-24 mt-4">
                <AssistBox />
              </div>
            )}
            {children}
            {showAssist && <AssistModal />}
          </>
        </AssistProvider>
      ) : children}
    </AssistProjectTypeContext.Provider>
  );
};

export default withSession(withConfig(MarketplaceAssist));
